import { CSSProperties } from "react";
import styled, { css, DefaultTheme } from "styled-components";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

/**
 * The new look's drawer building blocks (trade and signal drawers): the
 * progress well that leads the General tab, and the details as
 * hairline-separated groups of labels over figures.
 */

// ---- Progress well
export const ProgressWell = styled.div`
  padding: 20px 24px 24px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
`;
export const ProgressHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
export const ProgressLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
export const ProgressTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
export const ProgressFigures = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 8px;
`;
export const ProgressProfit = styled.span`
  ${newLookType.figureLarge};
  padding: 0 3px;
  margin: 0 -3px;
  border-radius: 4px;
`;
export const ProgressCaption = styled.span<{ $isWarning?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${newLookType.meta};
  color: ${(p) =>
    p.$isWarning
      ? getNewLookTokens(p.theme).stop
      : getNewLookTokens(p.theme).muted};
`;
export const PROGRESS_LADDER_STYLE = { marginTop: 16 };

// ---- Details groups
export const DetailsSection = styled.section`
  padding: 20px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
export const DetailsTitle = styled.h3`
  margin: 0 0 16px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
export const DetailsFields = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
  margin: 0;
`;
export const DetailsField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
export const DetailsLabel = styled.dt`
  display: flex;
  align-items: center;
  gap: 4px;
  ${newLookType.label};
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
export const DetailsValue = styled.dd`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin: 0;
  ${newLookType.figure};
  /* Under its own label each value stands alone: regular weight, so the
     grid reads as calm data rather than a block of bold. */
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
export const DetailsValueText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 359.98px) {
    white-space: normal;
    overflow-wrap: anywhere;
  }
  /* Shared value components (exchange with its logo) take the role. */
  .ant-typography,
  span {
    font-size: inherit !important;
    font-weight: inherit !important;
  }
`;
export const DetailsEmpty = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;

export const OrdersTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
export const OrdersCount = styled.span`
  ${newLookType.meta};
  font-weight: 500;
  min-width: 20px;
  padding: 1px 6px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  text-align: center;
`;
export const orderCard = css`
  padding: 10px 16px;
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  font-variant-numeric: tabular-nums;
  transition: background-color 120ms;
`;
export const newLookSortersStyle = (theme: DefaultTheme): CSSProperties => ({
  padding: "6px 16px",
  margin: "8px 0",
  background: theme.colors.filterChipsColor,
  borderRadius: theme.radii.control,
});
